// Reserved roles: fixed across every group regardless of its size, and excluded
// from the unit palette so a unit can never impersonate one of them.
export const MAIN_COLOR = "#4472C4";
// The unaccounted remainder is not a unit, so it wears the conventional "other"
// grey rather than a slot from the unit palette — the only achromatic fill in
// the chart, which is what makes it readable as the leftover.
export const UNACCOUNTED_COLOR = "#b7bcc4";
// Group-total line on the period chart. Kept darker than the meter palette so
// it stays readable on top of a dozen stacked segments.
export const TOTAL_LINE_COLOR = "#1D2A44";

/**
 * Twenty categorical fills for the unit meters, generated in OKLCH and checked
 * against the data-viz gates rather than picked by eye:
 *
 *   - OKLCH L within 0.43–0.77, chroma >= 0.10 (below that a hue reads as gray)
 *   - adjacent-pair separation under simulated protanopia and deuteranopia
 *     (Machado-Oliveira-Fernandes 2009, severity 1.0), OKLab dE x100 >= 8
 *   - adjacent-pair separation under normal vision >= 15
 *   - every slot also clears both gates against UNACCOUNTED_COLOR, which caps
 *     the stack, and against TOTAL_LINE_COLOR, which crosses it
 *
 * Worst adjacent pair: dE 14.3 CVD / 21.3 normal. The predecessor list (Kelly's
 * colours of maximum contrast) failed both gates at 6.3 / 13.9 — its three
 * near-yellows and its two grays were indistinguishable once stacked.
 *
 * The order is not cosmetic. It is optimised so that every prefix stands on its
 * own, because a group uses only as many slots as it has units: the first five
 * are mutually separated at roughly 1.5x the gate, the first ten at 0.8x, and
 * only a group past fifteen units leans on the adjacency guarantee alone.
 *
 * Twenty mutually distinct fills is past what colour alone can carry — the worst
 * pair across the whole list sits below the normal-vision floor. The 2px surface
 * gap between stacked segments and the per-day tooltip are the secondary
 * encoding that covers the difference.
 */
const UNIT_COLORS = [
  "#3e55df", // indigo
  "#419f05", // green
  "#923b30", // brick
  "#ff6d87", // coral
  "#11afff", // sky
  "#9e0179", // magenta
  "#0cad98", // teal
  "#8c71d7", // violet
  "#d52256", // crimson
  "#0683aa", // ocean
  "#d07d50", // clay
  "#594d99", // slate violet
  "#a4af0a", // citron
  "#ce45b5", // orchid
  "#866302", // olive gold
  "#d282d9", // light orchid
  "#9033bd", // purple
  "#007d54", // deep green
  "#2481ff", // blue
  "#aa5784", // dusty plum
];

export const UNIT_COLOR_COUNT = UNIT_COLORS.length;

// Blend a hex colour towards black or white. Used only past the 20th unit.
const mix = (hex, target, amount) => {
  const from = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const to = target === "dark" ? [0, 0, 0] : [255, 255, 255];
  return (
    "#" +
    from
      .map((channel, i) =>
        Math.round(channel + (to[i] - channel) * amount)
          .toString(16)
          .padStart(2, "0")
      )
      .join("")
  );
};

/**
 * Colour for the nth unit of a group. The first twenty are the validated list; a
 * group larger than that keeps going by shading the palette darker, then
 * lighter, so no two units ever land on the same colour.
 */
export const unitColor = (index) => {
  const base = UNIT_COLORS[index % UNIT_COLOR_COUNT];
  const cycle = Math.floor(index / UNIT_COLOR_COUNT);
  if (cycle === 0) return base;
  return mix(
    base,
    cycle % 2 === 1 ? "dark" : "light",
    Math.min(0.3 + 0.15 * Math.floor((cycle - 1) / 2), 0.75)
  );
};

export const TREND_SERIES = [
  { key: "three_month_average", name: "3 month average", color: "#548235" },
  { key: "previous_week", name: "Previous week", color: "#ED7D31" },
  { key: "current_week", name: "Current week", color: "#4472C4" },
];
